import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container,
  Paper,
  Grid,
  useTheme,
  alpha,
  Alert
} from '@mui/material';
import { 
  useNavigate, 
  useLocation 
} from 'react-router-dom';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { AppError } from '../utils/errorHandler';

const ErrorGeneric: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, isAuthenticated } = useAuth();

  // Extrair erro do state de navegação
  const errorState = location.state as { error?: AppError; message?: string } | undefined;
  const error = errorState?.error;
  const customMessage = errorState?.message;

  const handleGoHome = () => {
    if (isAuthenticated && user) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      handleGoHome();
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const errorMessage = customMessage || error?.friendlyMessage || 'Ocorreu um erro inesperado.';

  return (
    <Container maxWidth="md">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        textAlign="center"
        py={4}
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.1)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            width: '100%',
            maxWidth: 600
          }}
        >
          {/* Ícone de erro */}
          <Box sx={{ mb: 4 }}>
            <ErrorIcon 
              sx={{ 
                fontSize: 100, 
                color: theme.palette.warning.main,
                mb: 2,
                animation: 'shake 0.5s infinite'
              }} 
            />
          </Box>

          {/* Mensagem principal */}
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 2
            }}
          >
            Ops! Algo deu errado
          </Typography>

          <Typography 
            variant="body1" 
            color="text.secondary" 
            paragraph
            sx={{ fontSize: '1.1rem', mb: 4 }}
          >
            {errorMessage}
          </Typography>

          {/* Alert com detalhes adicionais (se houver) */}
          {error && error.status && (
            <Alert 
              severity={error.status >= 500 ? 'error' : 'warning'}
              icon={<WarningIcon />}
              sx={{ mb: 4, textAlign: 'left' }}
            >
              <Typography variant="body2">
                {error.status >= 500 
                  ? 'Este é um erro do servidor. Nossa equipe foi notificada.'
                  : 'Verifique os dados informados e tente novamente.'}
              </Typography>
            </Alert>
          )}

          {/* Botões de ação */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: theme.shadows[4]
                }}
              >
                Ir para o Início
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                size="large"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Voltar
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                size="large"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Recarregar
              </Button>
            </Grid>
          </Grid>

          {/* Informações do usuário (se logado) */}
          {isAuthenticated && user && (
            <Box 
              sx={{ 
                mt: 4, 
                pt: 3, 
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}` 
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Logado como: <strong>{user.name}</strong> ({user.role})
              </Typography>
            </Box>
          )}
        </Paper>

        {/* CSS para animação */}
        <style>
          {`
            @keyframes shake {
              0%, 100% { transform: translateX(0); }
              25% { transform: translateX(-5px); }
              75% { transform: translateX(5px); }
            }
          `}
        </style>
      </Box>
    </Container>
  );
};

export default ErrorGeneric;

